import React, { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "@/App";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_ORDER = ["owner", "manager", "admin", "sales"];
const ROLE_LABELS = { owner: "Owner", manager: "Manager", admin: "Admin", sales: "Sales" };
const ROLE_CLS = {
  owner: "bg-emerald-100 text-emerald-800 border-emerald-300",
  manager: "bg-violet-100 text-violet-800 border-violet-300",
  admin: "bg-blue-100 text-blue-800 border-blue-300",
  sales: "bg-indigo-100 text-indigo-800 border-indigo-300",
};
const ACTION_LABELS = {
  master: "Master Data (barang, pelanggan, supplier, sales)",
  inbound: "Barang Masuk",
  outbound: "Barang Keluar ke Sales",
  invoice_manage: "Edit/Hapus Invoice & Pembayaran/Cicilan",
  return_manage: "Retur & Pengembalian Armada",
  production: "Produksi & Resep",
  opname: "Buat Stock Opname",
  opening_balance: "Saldo Awal (buat/posting/cancel)",
  system_health: "Pemeriksaan Sistem (jalankan health check)",
  opname_approve: "Setujui Opname / Adjustment",
  audit: "Audit Trail",
};

export default function Settings() {
  const { user } = useContext(AuthContext);
  const isOwner = user?.role === "owner";
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([api.get("/users"), api.get("/permissions")]);
      setUsers(u.data);
      setPerms(p.data.permissions);
    } catch (e) { console.error("Gagal memuat pengaturan:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const deleteUser = async (u) => {
    if (!window.confirm(`Nonaktifkan akun ${u.name} (${u.email})?\n\nAkun tidak bisa login lagi, tetapi seluruh riwayat transaksi & audit trail-nya tetap tersimpan.`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success(`Akun ${u.name} dinonaktifkan`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus akun"); }
  };

  const setRole = async (u, role) => {
    try {
      await api.put(`/users/${u.user_id}/role`, { role });
      toast.success(`Role ${u.name} → ${ROLE_LABELS[role]}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah role"); }
  };

  const togglePerm = (action, role) => {
    if (!isOwner || role === "owner") return;
    setPerms((prev) => {
      const cur = new Set(prev[action] || []);
      if (cur.has(role)) cur.delete(role); else cur.add(role);
      return { ...prev, [action]: [...cur] };
    });
  };

  const savePerms = async () => {
    setSaving(true);
    try {
      await api.put("/permissions", { map: perms });
      toast.success("Hak akses tersimpan");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const resetAll = async () => {
    const typed = window.prompt("Ketik RESET untuk menghapus SELURUH data (barang, transaksi, pelanggan, stok, piutang). Akun & hak akses tetap tersimpan. Tindakan ini TIDAK dapat dibatalkan.");
    if (typed !== "RESET") { if (typed !== null) toast.error("Ketik RESET dengan huruf besar untuk melanjutkan"); return; }
    if (!window.confirm("Yakin? Semua data akan dihapus permanen.")) return;
    try {
      const r = await api.post("/admin/reset");
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Reset selesai — ${total} dokumen dihapus`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal reset"); }
  };

  return (
    <div data-testid="page-settings" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Akun & Aplikasi</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Pengaturan</h1>
      </div>

      <Card>
        <CardHeader><CardTitle>Profil Pengguna</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {user?.picture && <img src={user.picture} alt="" className="h-16 w-16 rounded-full" />}
            <div>
              <div className="font-semibold text-lg">{user?.name}</div>
              <div className="text-sm text-slate-500 font-mono">{user?.email}</div>
              <Badge className={`mt-1 ${ROLE_CLS[user?.role] || "bg-slate-100 text-slate-800"}`}>{ROLE_LABELS[user?.role] || user?.role}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="users-card">
        <CardHeader className="flex-row items-center gap-3 space-y-0">
          <ShieldCheck className="h-5 w-5 text-slate-500" />
          <div>
            <CardTitle>Pengguna & Hirarki Role</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Hirarki: Owner (tertinggi) → Manager → Admin & Sales (setara). Hanya Owner yang dapat mengubah role.</p>
          </div>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm" data-testid="table-users">
            <thead className="text-xs font-mono uppercase tracking-wider text-slate-500 text-left border-b">
              <tr><th className="py-2">Pengguna</th><th>Email</th><th>Role</th><th className="w-16"></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b">
                  <td className="py-2 font-medium">{u.name}</td>
                  <td className="font-mono text-xs text-slate-500">{u.email}</td>
                  <td>
                    {isOwner ? (
                      <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                        <SelectTrigger className="w-36 h-8" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLE_ORDER.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${ROLE_CLS[u.role]} font-normal`}>{ROLE_LABELS[u.role] || u.role}</Badge>
                    )}
                  </td>
                  <td className="text-right">
                    {isOwner && u.role !== "owner" && u.user_id !== user?.user_id && (
                      <Button variant="ghost" size="sm" onClick={() => deleteUser(u)} title="Nonaktifkan akun" data-testid={`delete-user-${u.user_id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {perms && (
        <Card data-testid="permissions-card">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Matriks Hak Akses</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Centang fitur yang boleh dipakai tiap role. Owner selalu memiliki semua akses.{!isOwner && " (Hanya Owner yang dapat mengubah)"}</p>
            </div>
            {isOwner && (
              <Button onClick={savePerms} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-permissions">
                <Save className="h-4 w-4 mr-1" />{saving ? "Menyimpan..." : "Simpan Hak Akses"}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-testid="table-permissions">
              <thead className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b">
                <tr><th className="py-2 text-left">Fitur</th>{ROLE_ORDER.map((r) => <th key={r} className="text-center w-24">{ROLE_LABELS[r]}</th>)}</tr>
              </thead>
              <tbody>
                {Object.keys(ACTION_LABELS).map((action) => (
                  <tr key={action} className="border-b">
                    <td className="py-2.5">{ACTION_LABELS[action]}</td>
                    {ROLE_ORDER.map((role) => {
                      const allowed = role === "owner" || (perms[action] || []).includes(role);
                      return (
                        <td key={role} className="text-center">
                          <input
                            type="checkbox"
                            checked={allowed}
                            disabled={role === "owner" || !isOwner}
                            onChange={() => togglePerm(action, role)}
                            className="h-4 w-4 accent-slate-900 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid={`perm-${action}-${role}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Tentang GudangKu</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2 leading-relaxed">
          <p>Aplikasi pergudangan sederhana untuk memantau stok gudang, armada sales, penjualan, produksi, dan retur — dengan rekonsiliasi otomatis (Neraca Barang) dan Stock Checker anti-selisih.</p>
          <p className="text-xs font-mono text-slate-400">Versi 1.0 · Bahasa Indonesia · Format Rupiah ID</p>
        </CardContent>
      </Card>

      {isOwner && (
        <Card className="border-rose-300 bg-rose-50/40" data-testid="danger-zone">
          <CardHeader><CardTitle className="text-rose-700">Zona Berbahaya</CardTitle></CardHeader>
          <CardContent className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-slate-600 max-w-xl">Menghapus <strong>SELURUH data</strong> (barang, transaksi, pelanggan, supplier, stok, piutang, opname). Akun pengguna & hak akses tetap tersimpan. <strong>Tidak dapat dibatalkan.</strong></p>
            <Button variant="destructive" onClick={resetAll} data-testid="btn-reset-data"><Trash2 className="h-4 w-4 mr-1" />Reset Semua Data</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
